import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { toast } from 'sonner';
import { SortableHeader, useSortableData } from '@/shared/components/ui/sortable-table';
import {
  Search,
  Plus,
  FileText,
  DollarSign,
  ChevronRight,
  ChevronDown,
  Download,
  Filter,
  Calendar,
  RefreshCw } from
'lucide-react';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger } from
'@/shared/components/ui/tabs';

const API_URL = API_BASE;

const getAuthHeaders = () => {
  const token = localStorage.getItem('ezerpToken');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

const typeColors = {
  asset: 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-400',
  liability: 'bg-red-100 text-red-700 dark:bg-red-900/50 dark:text-red-400',
  equity: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400',
  revenue: 'bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-400',
  expense: 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400'
};

export const GeneralLedger = () => {const { t } = useTranslation("finance");
  const [activeTab, setActiveTab] = useState('accounts');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedAccounts, setExpandedAccounts] = useState({});
  const [chartOfAccounts, setChartOfAccounts] = useState([]);
  const [journalEntries, setJournalEntries] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [accountsRes, entriesRes] = await Promise.all([
      fetch(`${API_URL}/api/finance/chart-of-accounts`, { headers: getAuthHeaders() }),
      fetch(`${API_URL}/api/finance/journal-entries`, { headers: getAuthHeaders() })]
      );
      if (!accountsRes.ok || !entriesRes.ok) throw new Error('Failed to fetch data');
      const [accountsData, entriesData] = await Promise.all([accountsRes.json(), entriesRes.json()]);
      setChartOfAccounts(accountsData);
      setJournalEntries(entriesData);
    } catch (err) {
      toast.error('Failed to load general ledger data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleAccount = (code) => {
    setExpandedAccounts((prev) => ({ ...prev, [code]: !prev[code] }));
  };

  const parentAccounts = chartOfAccounts.filter((a) => !a.parent);
  const getChildren = (parentCode) => chartOfAccounts.filter((a) => a.parent === parentCode);

  const totalAssets = chartOfAccounts.filter((a) => a.type === 'asset').reduce((sum, a) => sum + (a.balance || 0), 0);
  const totalLiabilities = chartOfAccounts.filter((a) => a.type === 'liability').reduce((sum, a) => sum + (a.balance || 0), 0);
  const totalEquity = chartOfAccounts.filter((a) => a.type === 'equity').reduce((sum, a) => sum + (a.balance || 0), 0);

  // Apply sorting to journal entries
  const { sortedData: sortedJournalEntries, sortConfig, requestSort } = useSortableData(journalEntries);

  const filteredJournalEntries = sortedJournalEntries.filter((entry) => {
    if (!searchTerm) return true;
    return entry.id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    entry.description?.toLowerCase().includes(searchTerm.toLowerCase());
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-500" />
      </div>);

  }

  return (
    <div className="space-y-6" data-testid="finance-general-ledger">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk']">{t("generalLedger", "General Ledger")}</h1>
          <p className="text-muted-foreground mt-1">{t("chartOfAccounts", "Chart of accounts and journal entries")}

          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={fetchData} className="gap-2">
            <RefreshCw className="h-4 w-4" />{t("refresh", "Refresh")}

          </Button>
          <Button variant="outline" className="gap-2">
            <Download className="h-4 w-4" />{t("export", "Export")}

          </Button>
          <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700">
            <Plus className="h-4 w-4" />{t("newEntry", "New Entry")}

          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="card-elevated border-l-4 border-l-blue-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("totalAssets", "Total Assets")}</p>
            <p className="text-2xl font-bold text-blue-600">${(totalAssets / 1000000).toFixed(2)}M</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-red-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("totalLiabilities", "Total Liabilities")}</p>
            <p className="text-2xl font-bold text-red-600">${(totalLiabilities / 1000).toFixed(0)}k</p>
          </CardContent>
        </Card>
        <Card className="card-elevated border-l-4 border-l-emerald-500">
          <CardContent className="p-4">
            <p className="text-sm text-muted-foreground">{t("totalEquity", "Total Equity")}</p>
            <p className="text-2xl font-bold text-emerald-600">${(totalEquity / 1000000).toFixed(2)}M</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="accounts" className="gap-2">
            <FileText className="h-4 w-4" />{t("chartOfAccounts", "Chart of Accounts")}

          </TabsTrigger>
          <TabsTrigger value="entries" className="gap-2">
            <DollarSign className="h-4 w-4" />{t("journalEntries", "Journal Entries")}

          </TabsTrigger>
        </TabsList>

        {/* Chart of Accounts Tab */}
        <TabsContent value="accounts" className="space-y-4">
          <Card className="card-elevated">
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground w-24">{t("code", "Code")}</th>
                    <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">{t("accountName", "Account Name")}</th>
                    <th className="text-center py-3 px-4 text-sm font-medium text-muted-foreground w-32">{t("type", "Type")}</th>
                    <th className="text-right py-3 px-4 text-sm font-medium text-muted-foreground w-40">{t("balance", "Balance")}</th>
                  </tr>
                </thead>
                <tbody>
                  {parentAccounts.map((account) => {
                    const children = getChildren(account.code);
                    const hasChildren = children.length > 0;
                    const isExpanded = expandedAccounts[account.code];

                    return (
                      <React.Fragment key={account.code}>
                        <tr
                          className="border-b border-border/50 hover:bg-muted/30 transition-colors cursor-pointer"
                          onClick={() => hasChildren && toggleAccount(account.code)}>
                          
                          <td className="py-3 px-4 font-mono text-sm">{account.code}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              {hasChildren && (
                              isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />)
                              }
                              <span className="font-medium">{account.name}</span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <Badge className={typeColors[account.type] || 'bg-gray-100 text-gray-700'}>
                              {account.type}
                            </Badge>
                          </td>
                          <td className="py-3 px-4 text-right font-mono font-medium">
                            ${account.balance?.toLocaleString()}
                          </td>
                        </tr>
                        {isExpanded && children.map((child) =>
                        <tr key={child.code} className="border-b border-border/50 bg-muted/10">
                            <td className="py-3 px-4 font-mono text-sm pl-8">{child.code}</td>
                            <td className="py-3 px-4 pl-12">{child.name}</td>
                            <td className="py-3 px-4 text-center">
                              <Badge variant="secondary">{child.type}</Badge>
                            </td>
                            <td className="py-3 px-4 text-right font-mono">
                              ${child.balance?.toLocaleString()}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>);

                  })}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Journal Entries Tab */}
        <TabsContent value="entries" className="space-y-4">
          <Card className="card-elevated">
            <CardHeader className="pb-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="text-lg">{t("journalEntries", "Journal Entries")}</CardTitle>
                <div className="relative w-full sm:w-64">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Search entries..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10" />
                  
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted/30">
                    <SortableHeader column="id" label="Entry ID" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="date" label="Date" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="description" label="Description" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="debitAccount" label="Debit Account" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="creditAccount" label="Credit Account" sortConfig={sortConfig} onSort={requestSort} />
                    <SortableHeader column="amount" label="Amount" sortConfig={sortConfig} onSort={requestSort} align="right" />
                    <SortableHeader column="status" label="Status" sortConfig={sortConfig} onSort={requestSort} align="center" />
                  </tr>
                </thead>
                <tbody>
                  {filteredJournalEntries.map((entry) =>
                  <tr key={entry.id} className="border-b border-border/50 hover:bg-muted/30 transition-colors">
                      <td className="py-3 px-4 font-mono text-sm font-medium">{entry.id}</td>
                      <td className="py-3 px-4 text-sm">
                        <div className="flex items-center gap-1.5">
                          <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                          {entry.date}
                        </div>
                      </td>
                      <td className="py-3 px-4 text-sm">{entry.description}</td>
                      <td className="py-3 px-4 font-mono text-sm">{entry.debit_account}</td>
                      <td className="py-3 px-4 font-mono text-sm">{entry.credit_account}</td>
                      <td className="py-3 px-4 text-right font-mono font-medium">
                        ${entry.amount?.toLocaleString()}
                      </td>
                      <td className="py-3 px-4 text-center">
                        <Badge className={entry.status === 'posted' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
                          {entry.status}
                        </Badge>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>);

};